import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  FiBell,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiX,
  FiUser,
  FiTruck,
  FiUsers,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiSend,
  FiInbox,
  FiEye,
  FiEyeOff,
  FiRefreshCw,
} from "react-icons/fi";
import {
  getAllPostNotifications,
  createNotification,
  createBulkNotifications,
  deleteNotification,
  markNotificationAsRead,
} from "../api/notificationApi";

const LIMIT = 10;

// ── Notification type options ──────────────────────────────────────────────
const NOTIF_TYPES = [
  { value: "general", label: "General" },
  { value: "promo", label: "Promo / Offer" },
  { value: "system", label: "System / Maintenance" },
  { value: "ride_requested", label: "Ride Requested" },
  { value: "ride_accepted", label: "Ride Accepted" },
  { value: "ride_cancelled", label: "Ride Cancelled" },
  { value: "trip_completed", label: "Trip Completed" },
  { value: "payment_received", label: "Payment Received" },
  { value: "wallet_credited", label: "Wallet Credited" },
  { value: "sos_alert", label: "SOS Alert" },
  { value: "sos_resolved", label: "SOS Resolved" },
  { value: "rating_reminder", label: "Rating Reminder" },
];

const PRIORITY_OPTS = ["low", "normal", "high"];

const CHANNEL_OPTS = [
  { value: "both", label: "Push + In-App" },
  { value: "push", label: "Push Only" },
  { value: "in_app", label: "In-App Only" },
];

const STATUS_COLORS = {
  pending:   { bg: "#FFF8E1", color: "#B8860B" },
  sent:      { bg: "#E3F2FD", color: "#1565C0" },
  delivered: { bg: "#E8F5E9", color: "#2E7D32" },
  read:      { bg: "#F3E5F5", color: "#6A1B9A" },
  failed:    { bg: "#FFEBEE", color: "#C62828" },
};

const TYPE_ICONS = {
  passenger: <FiUser  size={13} />,
  driver:    <FiTruck size={13} />,
};

// ── Inline styles ─────────────────────────────────────────────────────────
const S = {
  page: {
    padding: "28px 32px",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    background: "#F7F8FA",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  title: { fontSize: 22, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 10 },
  subtitle: { fontSize: 13, color: "#6B7280", marginTop: 3 },

  statsRow: { display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" },
  statCard: (color) => ({
    background: "#fff",
    border: `1.5px solid ${color}22`,
    borderRadius: 12,
    padding: "14px 20px",
    flex: "1 1 120px",
    minWidth: 120,
  }),
  statNum: (color) => ({ fontSize: 26, fontWeight: 800, color }),
  statLabel: { fontSize: 12, color: "#9CA3AF", marginTop: 2 },

  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    padding: "20px 24px",
    marginBottom: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 16, display: "flex", alignItems: "center", gap: 7 },

  row: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 5, flex: "1 1 160px", minWidth: 140 },
  label: { fontSize: 12, fontWeight: 600, color: "#374151" },
  input: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    transition: "border .15s",
    background: "#fff",
  },
  select: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    background: "#fff",
    cursor: "pointer",
  },
  textarea: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    transition: "border .15s",
  },
  btnPrimary: {
    background: "#1d9e75",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  btnDanger: {
    background: "#FEE2E2",
    color: "#DC2626",
    border: "none",
    borderRadius: 6,
    padding: "5px 8px",
    fontSize: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  btnGhost: {
    background: "transparent",
    color: "#6B7280",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#fff",
    flex: "1 1 200px",
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: 13,
    flex: 1,
    background: "transparent",
  },
  filterTab: (active) => ({
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "#1d9e75" : "#fff",
    color: active ? "#fff" : "#6B7280",
    transition: "all .15s",
  }),
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    padding: "10px 12px",
    borderBottom: "1px solid #F3F4F6",
    background: "#F9FAFB",
  },
  td: { padding: "11px 12px", fontSize: 13, color: "#374151", borderBottom: "1px solid #F3F4F6", verticalAlign: "middle" },
  badge: (status) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: (STATUS_COLORS[status] || STATUS_COLORS.pending).bg,
    color: (STATUS_COLORS[status] || STATUS_COLORS.pending).color,
  }),
  recipientBadge: (type) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 9px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: type === "passenger" ? "#EEF2FF" : "#FFF7ED",
    color: type === "passenger" ? "#4338CA" : "#C2410C",
  }),
  priorityDot: (p) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    background: p === "high" ? "#EF4444" : p === "normal" ? "#F59E0B" : "#10B981",
  }),
  pagination: { display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 20 },
  pageBtn: (active) => ({
    width: 34,
    height: 34,
    borderRadius: 8,
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "#1d9e75" : "#fff",
    color: active ? "#fff" : "#374151",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  toast: (type) => ({
    position: "fixed",
    top: 24,
    right: 24,
    zIndex: 9999,
    background: type === "success" ? "#1d9e75" : "#DC2626",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 20px rgba(0,0,0,.15)",
    animation: "slideIn .25s ease",
  }),
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#9CA3AF",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
export default function NotificationPage() {
 

  // ── List States ───────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, read: 0, failed: 0, sent: 0 });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0, hasNextPage: false, hasPrevPage: false });
  const [loading, setLoading] = useState(true);

  // ── Filter States ─────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]         = useState("");
  const [filterStatus, setFilterStatus]     = useState("");
  const [filterType, setFilterType]         = useState("");
  const [filterRecipient, setFilterRecipient] = useState("");
  const [currentPage, setCurrentPage]       = useState(1);
  const debounceRef = useRef(null);

  // ── Compose Form ──────────────────────────────────────────────────────────
  const [showCompose, setShowCompose] = useState(false);
  const [sendMode, setSendMode]       = useState("single"); // "single" | "bulk"
  const [submitting, setSubmitting]   = useState(false);
  const [formErrors, setFormErrors]   = useState({});

  const defaultForm = {
    recipientId: "",
    recipientType: "passenger",
    type: "general",
    title: "",
    body: "",
    channel: "both",
    priority: "normal",
    rideId: "",
    sosId: "",
  };
  const [form, setForm] = useState(defaultForm);

  // ── Bulk Compose ──────────────────────────────────────────────────────────
  const [bulkForm, setBulkForm] = useState({
    recipientType: "passenger",   // "passenger" | "driver" | "both"
    recipientIds: "",             // comma-separated IDs (optional — empty = broadcast via server)
    type: "general",
    title: "",
    body: "",
    channel: "both",
    priority: "normal",
  });

  // ── Preview Modal ──────────────────────────────────────────────────────────
  const [previewNotif, setPreviewNotif] = useState(null);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  FETCH
  // ─────────────────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async (page, search, status, type, recipientType) => {
    try {
      setLoading(true);
      const payload = {
        n_page: page,
        n_limit: LIMIT,
        c_search_term: (search || "").trim(),
        c_status: status || "",
        c_type: type || "",
        c_recipient_type: recipientType || "",
      };
      const res = await getAllPostNotifications(payload);
      if (!res.success) { setNotifications([]); return; }
      setNotifications(res.notifications || []);
      setPagination(res.pagination || { currentPage: 1, totalPages: 1, totalCount: 0, hasNextPage: false, hasPrevPage: false });
      if (res.stats) setStats(res.stats);
    } catch (err) {
      console.error(err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(1, "", "", "", ""); }, [fetchNotifications]);

  // ── Debounced search ──────────────────────────────────────────────────────
  const handleSearch = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    setCurrentPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchNotifications(1, v, filterStatus, filterType, filterRecipient), 400);
  };

  const applyFilter = (key, val) => {
    const next = { status: filterStatus, type: filterType, recipient: filterRecipient, [key]: val };
    setFilterStatus(next.status);
    setFilterType(next.type);
    setFilterRecipient(next.recipient);
    setCurrentPage(1);
    fetchNotifications(1, searchTerm, next.status, next.type, next.recipient);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setCurrentPage(page);
    fetchNotifications(page, searchTerm, filterStatus, filterType, filterRecipient);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  SEND SINGLE
  // ─────────────────────────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!form.recipientId.trim()) e.recipientId = "Recipient ID required";
    if (!form.title.trim()) e.title = "Title required";
    if (!form.body.trim()) e.body = "Message body required";
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSendSingle = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        rideId: form.rideId || null,
        sosId: form.sosId || null,
      };
      const res = await createNotification(payload);
      if (res.success) {
        showToast("Notification sent successfully!");
        setShowCompose(false);
        setForm(defaultForm);
        setFormErrors({});
        fetchNotifications(currentPage, searchTerm, filterStatus, filterType, filterRecipient);
      } else {
        showToast(res.error || "Failed to send", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  SEND BULK
  // ─────────────────────────────────────────────────────────────────────────
  const handleSendBulk = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!bulkForm.title.trim()) e.bulkTitle = "Title required";
    if (!bulkForm.body.trim())  e.bulkBody  = "Body required";
    if (Object.keys(e).length) { setFormErrors(e); return; }

    // Build recipient list
    let recipientTypes = [];
    if (bulkForm.recipientType === "both") recipientTypes = ["passenger", "driver"];
    else recipientTypes = [bulkForm.recipientType];

    const idsRaw = bulkForm.recipientIds.trim();
    const ids = idsRaw ? idsRaw.split(",").map(s => s.trim()).filter(Boolean) : [];

    let notifications = [];
    if (ids.length > 0) {
      // Specific IDs
      ids.forEach(id => {
        recipientTypes.forEach(rt => {
          notifications.push({
            recipientId: id,
            recipientType: rt,
            type: bulkForm.type,
            title: bulkForm.title,
            body: bulkForm.body,
            channel: bulkForm.channel,
            priority: bulkForm.priority,
          });
        });
      });
    } else {
      // No IDs → create a general broadcast entry per type
      // Server side-la handle panna; here we send 1 per type with recipientId = "broadcast"
      recipientTypes.forEach(rt => {
        notifications.push({
          recipientId: "broadcast",
          recipientType: rt,
          type: bulkForm.type,
          title: bulkForm.title,
          body: bulkForm.body,
          channel: bulkForm.channel,
          priority: bulkForm.priority,
        });
      });
    }

    setSubmitting(true);
    try {
      const res = await createBulkNotifications({ notifications });
      if (res.success) {
        showToast(`${res.count || notifications.length} notifications sent!`);
        setShowCompose(false);
        setBulkForm({ recipientType: "passenger", recipientIds: "", type: "general", title: "", body: "", channel: "both", priority: "normal" });
        setFormErrors({});
        fetchNotifications(currentPage, searchTerm, filterStatus, filterType, filterRecipient);
      } else {
        showToast(res.error || "Failed to send bulk", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  DELETE
  // ─────────────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    const res = await deleteNotification(id);
    if (res.success) {
      showToast("Deleted successfully");
      fetchNotifications(currentPage, searchTerm, filterStatus, filterType, filterRecipient);
    } else {
      showToast(res.error || "Delete failed", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  MARK READ
  // ─────────────────────────────────────────────────────────────────────────
  const handleMarkRead = async (notificationId) => {
    await markNotificationAsRead(notificationId);
    setNotifications(prev => prev.map(n => n.notificationId === notificationId ? { ...n, isRead: true, status: "read" } : n));
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  HELPERS
  // ─────────────────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) + " " +
           dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const truncate = (str, n = 45) => str && str.length > n ? str.slice(0, n) + "…" : str;

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <style>{`
        @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        .notif-tr:hover { background: #FAFBFC; }
        .notif-input:focus { border-color: #1d9e75 !important; box-shadow: 0 0 0 3px #1d9e7520; }
        .notif-btn-primary:hover { background: #178c66 !important; }
        .notif-btn-ghost:hover { background: #F3F4F6 !important; }
        .compose-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; }
        .compose-modal { background:#fff; border-radius:16px; padding:28px 32px; width:100%; max-width:560px; max-height:90vh; overflow-y:auto; }
        .preview-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1001; display:flex; align-items:center; justify-content:center; }
        .preview-modal { background:#fff; border-radius:16px; padding:28px 32px; width:100%; max-width:480px; }
        .mode-tab { padding:8px 20px; border-radius:8px; border:none; font-size:13px; font-weight:600; cursor:pointer; transition:all .15s; }
        .mode-tab.active { background:#1d9e75; color:#fff; }
        .mode-tab.inactive { background:#F3F4F6; color:#6B7280; }
        .mode-tab.inactive:hover { background:#E5E7EB; }
      `}</style>

      {/* Toast */}
      {toast.show && (
        <div style={S.toast(toast.type)}>
          {toast.type === "success" ? <FiCheckCircle size={16} /> : <FiAlertCircle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>
            <FiBell size={22} color="#1d9e75" />
            Notification Center
          </div>
          <div style={S.subtitle}>Send & manage notifications for passengers and drivers</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            style={S.btnGhost}
            className="notif-btn-ghost"
            onClick={() => fetchNotifications(currentPage, searchTerm, filterStatus, filterType, filterRecipient)}
          >
            <FiRefreshCw size={14} /> Refresh
          </button>
          <button
            style={S.btnPrimary}
            className="notif-btn-primary"
            onClick={() => { setShowCompose(true); setFormErrors({}); }}
          >
            <FiPlus size={15} /> Send Notification
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        {[
          { label: "Total",    value: stats.total,  color: "#6366F1" },
          { label: "Unread",   value: stats.unread, color: "#F59E0B" },
          { label: "Read",     value: stats.read,   color: "#10B981" },
          { label: "Sent",     value: stats.sent,   color: "#3B82F6" },
          { label: "Failed",   value: stats.failed, color: "#EF4444" },
        ].map(s => (
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={S.statNum(s.color)}>{s.value}</div>
            <div style={S.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ ...S.card, padding: "16px 20px" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          {/* Search */}
          <div style={S.searchBox}>
            <FiSearch size={14} color="#9CA3AF" />
            <input
              style={S.searchInput}
              placeholder="Search title, body, recipient ID…"
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm && (
              <FiX size={14} style={{ cursor: "pointer", color: "#9CA3AF" }}
                onClick={() => { setSearchTerm(""); fetchNotifications(1, "", filterStatus, filterType, filterRecipient); }} />
            )}
          </div>

          {/* Status filter */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["", "pending", "sent", "delivered", "read", "failed"].map(s => (
              <button key={s} style={S.filterTab(filterStatus === s)}
                onClick={() => applyFilter("status", s)}>
                {s || "All Status"}
              </button>
            ))}
          </div>

          {/* Recipient type */}
          <select style={{ ...S.select, minWidth: 130 }} value={filterRecipient}
            onChange={e => applyFilter("recipient", e.target.value)}>
            <option value="">All Recipients</option>
            <option value="passenger">Passenger</option>
            <option value="driver">Driver</option>
          </select>

          {/* Type filter */}
          <select style={{ ...S.select, minWidth: 140 }} value={filterType}
            onChange={e => applyFilter("type", e.target.value)}>
            <option value="">All Types</option>
            {NOTIF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        <div style={{ ...S.cardTitle, justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <FiInbox size={15} /> Notifications
            <span style={{ background: "#F3F4F6", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#6B7280" }}>
              {pagination.totalCount}
            </span>
          </span>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>
            <FiRefreshCw size={24} style={{ animation: "spin 1s linear infinite" }} />
            <div style={{ marginTop: 10 }}>Loading…</div>
          </div>
        ) : notifications.length === 0 ? (
          <div style={S.emptyState}>
            <FiBell size={40} color="#E5E7EB" />
            <div style={{ marginTop: 12, fontWeight: 600 }}>No notifications found</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Try changing filters or send a new notification</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["#", "Recipient", "Type", "Title", "Message", "Priority", "Status", "Sent At", "Actions"].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {notifications.map((n, idx) => (
                  <tr key={n._id} className="notif-tr">
                    <td style={S.td}>{(currentPage - 1) * LIMIT + idx + 1}</td>
                    <td style={S.td}>
                      <div style={S.recipientBadge(n.recipientType)}>
                        {TYPE_ICONS[n.recipientType]}
                        {n.recipientType}
                      </div>
                      <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>{truncate(n.recipientId, 18)}</div>
                    </td>
                    <td style={S.td}>
                      <span style={{ fontSize: 11, background: "#F3F4F6", padding: "3px 8px", borderRadius: 6, color: "#374151" }}>
                        {n.type?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td style={{ ...S.td, maxWidth: 180 }}>
                      <span style={{ fontWeight: 600 }}>{truncate(n.title, 30)}</span>
                    </td>
                    <td style={{ ...S.td, maxWidth: 200, color: "#6B7280" }}>
                      {truncate(n.body, 40)}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={S.priorityDot(n.priority)} />
                        {n.priority}
                      </div>
                    </td>
                    <td style={S.td}>
                      <span style={S.badge(n.status)}>{n.status}</span>
                      {!n.isRead && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: "#FEF3C7", color: "#D97706", padding: "2px 6px", borderRadius: 10, fontWeight: 700 }}>
                          UNREAD
                        </span>
                      )}
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: "#9CA3AF", whiteSpace: "nowrap" }}>
                      {formatDate(n.sentAt || n.createdAt)}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          title="View"
                          style={{ ...S.btnDanger, background: "#EFF6FF", color: "#2563EB" }}
                          onClick={() => setPreviewNotif(n)}
                        >
                          <FiEye size={13} />
                        </button>
                        {!n.isRead && (
                          <button
                            title="Mark Read"
                            style={{ ...S.btnDanger, background: "#F0FDF4", color: "#16A34A" }}
                            onClick={() => handleMarkRead(n.notificationId)}
                          >
                            <FiEyeOff size={13} />
                          </button>
                        )}
                        <button
                          title="Delete"
                          style={S.btnDanger}
                          onClick={() => handleDelete(n._id)}
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={S.pagination}>
            <button style={S.pageBtn(false)} disabled={!pagination.hasPrevPage}
              onClick={() => handlePageChange(currentPage - 1)}>
              <FiChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
              let page = i + 1;
              if (pagination.totalPages > 7) {
                const half = 3;
                const start = Math.max(1, Math.min(currentPage - half, pagination.totalPages - 6));
                page = start + i;
              }
              return (
                <button key={page} style={S.pageBtn(page === currentPage)}
                  onClick={() => handlePageChange(page)}>
                  {page}
                </button>
              );
            })}
            <button style={S.pageBtn(false)} disabled={!pagination.hasNextPage}
              onClick={() => handlePageChange(currentPage + 1)}>
              <FiChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* ── Compose Modal ─────────────────────────────────────────────────── */}
      {showCompose && (
        <div className="compose-overlay" onClick={() => !submitting && setShowCompose(false)}>
          <div className="compose-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
                <FiSend size={18} color="#1d9e75" /> Send Notification
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}
                onClick={() => !submitting && setShowCompose(false)}>
                <FiX size={20} />
              </button>
            </div>

            {/* Mode Tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 22, background: "#F9FAFB", padding: 4, borderRadius: 10 }}>
              <button className={`mode-tab ${sendMode === "single" ? "active" : "inactive"}`}
                style={{ flex: 1 }} onClick={() => setSendMode("single")}>
                <FiUser size={13} style={{ marginRight: 5 }} /> Single
              </button>
              <button className={`mode-tab ${sendMode === "bulk" ? "active" : "inactive"}`}
                style={{ flex: 1 }} onClick={() => setSendMode("bulk")}>
                <FiUsers size={13} style={{ marginRight: 5 }} /> Bulk / Broadcast
              </button>
            </div>

            {/* ── Single Form ── */}
            {sendMode === "single" && (
              <form onSubmit={handleSendSingle}>
                <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <div style={{ ...S.fieldGroup, flex: 2 }}>
                    <label style={S.label}>Recipient ID *</label>
                    <input className="notif-input" style={{ ...S.input, borderColor: formErrors.recipientId ? "#EF4444" : "#D1D5DB" }}
                      placeholder="passengerId or driverId"
                      value={form.recipientId}
                      onChange={e => { setForm({ ...form, recipientId: e.target.value }); setFormErrors({ ...formErrors, recipientId: "" }); }} />
                    {formErrors.recipientId && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.recipientId}</span>}
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Recipient Type *</label>
                    <select style={S.select} value={form.recipientType}
                      onChange={e => setForm({ ...form, recipientType: e.target.value })}>
                      <option value="passenger">Passenger</option>
                      <option value="driver">Driver</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Type *</label>
                    <select style={S.select} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      {NOTIF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Priority</label>
                    <select style={S.select} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                      {PRIORITY_OPTS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Channel</label>
                    <select style={S.select} value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>
                      {CHANNEL_OPTS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ ...S.fieldGroup, marginBottom: 14 }}>
                  <label style={S.label}>Title *</label>
                  <input className="notif-input" style={{ ...S.input, borderColor: formErrors.title ? "#EF4444" : "#D1D5DB" }}
                    placeholder="Notification title"
                    value={form.title}
                    onChange={e => { setForm({ ...form, title: e.target.value }); setFormErrors({ ...formErrors, title: "" }); }} />
                  {formErrors.title && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.title}</span>}
                </div>

                <div style={{ ...S.fieldGroup, marginBottom: 14 }}>
                  <label style={S.label}>Message Body *</label>
                  <textarea className="notif-input" style={{ ...S.textarea, borderColor: formErrors.body ? "#EF4444" : "#D1D5DB", width: "100%" }}
                    rows={3} placeholder="Notification message…"
                    value={form.body}
                    onChange={e => { setForm({ ...form, body: e.target.value }); setFormErrors({ ...formErrors, body: "" }); }} />
                  {formErrors.body && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.body}</span>}
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Ride ID (optional)</label>
                    <input className="notif-input" style={S.input} placeholder="RIDE-XXXXXXXX"
                      value={form.rideId} onChange={e => setForm({ ...form, rideId: e.target.value })} />
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>SOS ID (optional)</label>
                    <input className="notif-input" style={S.input} placeholder="SOS-XXXXXXXX"
                      value={form.sosId} onChange={e => setForm({ ...form, sosId: e.target.value })} />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button type="button" style={S.btnGhost} className="notif-btn-ghost"
                    onClick={() => { setShowCompose(false); setForm(defaultForm); setFormErrors({}); }}>
                    Cancel
                  </button>
                  <button type="submit" style={S.btnPrimary} className="notif-btn-primary" disabled={submitting}>
                    {submitting ? "Sending…" : <><FiSend size={14} /> Send</>}
                  </button>
                </div>
              </form>
            )}

            {/* ── Bulk Form ── */}
            {sendMode === "bulk" && (
              <form onSubmit={handleSendBulk}>
                <div style={{ background: "#FFF8E1", border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400E", marginBottom: 16 }}>
                  <FiAlertCircle size={13} style={{ marginRight: 5, verticalAlign: "middle" }} />
                  Bulk send records notifications in DB. For actual FCM push to all devices, your backend must loop through recipients' FCM tokens.
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Send To</label>
                    <select style={S.select} value={bulkForm.recipientType}
                      onChange={e => setBulkForm({ ...bulkForm, recipientType: e.target.value })}>
                      <option value="passenger">All Passengers</option>
                      <option value="driver">All Drivers</option>
                      <option value="both">All (Passengers + Drivers)</option>
                    </select>
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Type</label>
                    <select style={S.select} value={bulkForm.type} onChange={e => setBulkForm({ ...bulkForm, type: e.target.value })}>
                      {NOTIF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ ...S.fieldGroup, marginBottom: 14 }}>
                  <label style={S.label}>Specific IDs (optional — comma separated)</label>
                  <input className="notif-input" style={S.input}
                    placeholder="ID1, ID2, ID3  — leave blank for broadcast"
                    value={bulkForm.recipientIds}
                    onChange={e => setBulkForm({ ...bulkForm, recipientIds: e.target.value })} />
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>Leave blank to create a broadcast record</span>
                </div>

                <div style={{ ...S.fieldGroup, marginBottom: 14 }}>
                  <label style={S.label}>Title *</label>
                  <input className="notif-input" style={{ ...S.input, borderColor: formErrors.bulkTitle ? "#EF4444" : "#D1D5DB" }}
                    placeholder="Notification title"
                    value={bulkForm.title}
                    onChange={e => { setBulkForm({ ...bulkForm, title: e.target.value }); setFormErrors({ ...formErrors, bulkTitle: "" }); }} />
                  {formErrors.bulkTitle && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.bulkTitle}</span>}
                </div>

                <div style={{ ...S.fieldGroup, marginBottom: 14 }}>
                  <label style={S.label}>Message Body *</label>
                  <textarea className="notif-input" style={{ ...S.textarea, borderColor: formErrors.bulkBody ? "#EF4444" : "#D1D5DB", width: "100%" }}
                    rows={3} placeholder="Notification message…"
                    value={bulkForm.body}
                    onChange={e => { setBulkForm({ ...bulkForm, body: e.target.value }); setFormErrors({ ...formErrors, bulkBody: "" }); }} />
                  {formErrors.bulkBody && <span style={{ fontSize: 11, color: "#EF4444" }}>{formErrors.bulkBody}</span>}
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Priority</label>
                    <select style={S.select} value={bulkForm.priority} onChange={e => setBulkForm({ ...bulkForm, priority: e.target.value })}>
                      {PRIORITY_OPTS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div style={S.fieldGroup}>
                    <label style={S.label}>Channel</label>
                    <select style={S.select} value={bulkForm.channel} onChange={e => setBulkForm({ ...bulkForm, channel: e.target.value })}>
                      {CHANNEL_OPTS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button type="button" style={S.btnGhost} className="notif-btn-ghost"
                    onClick={() => { setShowCompose(false); setFormErrors({}); }}>
                    Cancel
                  </button>
                  <button type="submit" style={S.btnPrimary} className="notif-btn-primary" disabled={submitting}>
                    {submitting ? "Sending…" : <><FiUsers size={14} /> Send Bulk</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Preview Modal ────────────────────────────────────────────────── */}
      {previewNotif && (
        <div className="preview-overlay" onClick={() => setPreviewNotif(null)}>
          <div className="preview-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Notification Detail</div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}
                onClick={() => setPreviewNotif(null)}><FiX size={20} /></button>
            </div>
            {[
              ["ID", previewNotif.notificationId],
              ["Recipient ID", previewNotif.recipientId],
              ["Recipient Type", previewNotif.recipientType],
              ["Type", previewNotif.type?.replace(/_/g, " ")],
              ["Title", previewNotif.title],
              ["Message", previewNotif.body],
              ["Status", previewNotif.status],
              ["Priority", previewNotif.priority],
              ["Channel", previewNotif.channel],
              ["Is Read", previewNotif.isRead ? "Yes" : "No"],
              ["Ride ID", previewNotif.rideId || "—"],
              ["SOS ID", previewNotif.sosId || "—"],
              ["Created", formatDate(previewNotif.createdAt)],
              ["Sent At", formatDate(previewNotif.sentAt)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #F3F4F6" }}>
                <div style={{ width: 110, fontSize: 12, fontWeight: 600, color: "#9CA3AF", flexShrink: 0 }}>{k}</div>
                <div style={{ fontSize: 13, color: "#374151", wordBreak: "break-all" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}